import { useTranslation } from "react-i18next";
import Card from "../../../../../components/ui/Card";
import { Branch } from "../../../../../utils/supabaseClient";
import Toggle from "../../../../../components/ui/Toggle";
import { OperatingHours } from "../../../../../types/restaurant";
import { OperatingHoursType, ArrivalTimeSettings } from "../../../../../types/custom";
import { useEffect, useState } from "react";
import { defaultOperatingHours } from "../../../../../utils/defaultValues";
import { FiPlus, FiMinus } from "react-icons/fi";


type OperatingHoursSettingsProps = {
	restaurant: Branch | null;
	updateRestaurant: (updates: Partial<Branch>) => void;
}

const OperatingHoursSettings: React.FC<OperatingHoursSettingsProps> = ({
	restaurant, updateRestaurant
}) => {
	const { t } = useTranslation();
	const [arrivalTimeSettings, setArrivalTimeSettings] = useState<ArrivalTimeSettings>({
		is_enabled: false,
		time_options: ['00:15', '00:30', '00:45', '01:00'],
		default_selection: 0
	});

	// Initialize arrival time from restaurant data
	useEffect(() => {
		if (restaurant?.arrival_time) {
			setArrivalTimeSettings(restaurant.arrival_time as ArrivalTimeSettings);
		}
	}, [restaurant?.arrival_time]);

	const updateOperatingHours = (dayIndex: number, updates: Partial<OperatingHours>) => {
		const currentHours = restaurant?.operating_hours as OperatingHoursType[] || [];
		const newHours = [...currentHours];
		const currentItem = newHours[dayIndex] as OperatingHours;
		newHours[dayIndex] = { ...currentItem, ...updates };
		updateRestaurant({
			operating_hours: newHours as any
		});
	};

	// Save arrival time settings to the branch
	const saveArrivalTimeSettings = () => {
		updateRestaurant({
			arrival_time: arrivalTimeSettings as any
		});
	};

	// Update arrival time and save
	const handleArrivalTimeChange = (newSettings: ArrivalTimeSettings) => {
		setArrivalTimeSettings(newSettings);
		// Save immediately on change
		updateRestaurant({
			arrival_time: newSettings as any
		});
	};

	useEffect(() => {
		if (restaurant && !restaurant.operating_hours) {
			updateRestaurant({
				operating_hours: defaultOperatingHours as any
			})
		}
	}, [restaurant?.id])

	return <div className="space-y-6">
		<Card>
			<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
				{t('settings.operatingHours')}
			</h3>
			
			{/* Customer Arrival Time Settings */}
			<div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
				<div className="flex items-center justify-between mb-3">
					<div>
						<label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
							{t('settings.customerArrivalTime')}
						</label>
						<p className="text-xs text-gray-600 dark:text-gray-400">
							{t('settings.customerArrivalTimeDescription')}
						</p>
					</div>
					<Toggle
						enabled={arrivalTimeSettings.is_enabled}
						onChange={(enabled) => handleArrivalTimeChange({
							...arrivalTimeSettings,
							is_enabled: enabled
						})}
					/>
				</div>

				{arrivalTimeSettings.is_enabled && (
					<div className="space-y-3 mt-4">
						<div className="flex items-center justify-between mb-2">
							<label className="text-xs font-medium text-gray-700 dark:text-gray-300">
								{t('settings.timeOptions')}
							</label>
							<button
								onClick={() => handleArrivalTimeChange({
									...arrivalTimeSettings,
									time_options: [...arrivalTimeSettings.time_options, '02:00']
								})}
								className="flex items-center gap-1 px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90 transition-colors"
							>
								<FiPlus className="w-3 h-3" />
								{t('settings.addTimeOption')}
							</button>
						</div>

						<div className="space-y-2">
							{arrivalTimeSettings.time_options.map((timeOption, optionIndex) => (
								<div key={`time-option-${optionIndex}`} className="flex items-center gap-2">
									<input
										type="text"
										value={timeOption}
										placeholder="00:00"
										onChange={(e) => {
											const newTimeOptions = [...arrivalTimeSettings.time_options];
											newTimeOptions[optionIndex] = e.target.value;
											handleArrivalTimeChange({
												...arrivalTimeSettings,
												time_options: newTimeOptions
											});
										}}
										className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
									/>
									<input
										type="radio"
										name="default-arrival-time"
										checked={arrivalTimeSettings.default_selection === optionIndex}
										onChange={() => handleArrivalTimeChange({
											...arrivalTimeSettings,
											default_selection: optionIndex
										})}
										className="w-4 h-4 cursor-pointer"
									/>
									<span className="text-xs text-gray-500 dark:text-gray-400">
										{optionIndex === arrivalTimeSettings.default_selection ? t('settings.default') : ''}
									</span>
									{arrivalTimeSettings.time_options.length > 1 && (
										<button
											onClick={() => {
												const newTimeOptions = arrivalTimeSettings.time_options.filter((_, i) => i !== optionIndex);
												let newDefaultSelection = arrivalTimeSettings.default_selection;
												if (newDefaultSelection >= newTimeOptions.length) {
													newDefaultSelection = newTimeOptions.length - 1;
												}
												handleArrivalTimeChange({
													...arrivalTimeSettings,
													time_options: newTimeOptions,
													default_selection: newDefaultSelection
												});
											}}
											className="ml-auto p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
										>
											<FiMinus className="w-4 h-4" />
										</button>
									)}
								</div>
							))}
						</div>
					</div>
				)}
			</div>
			<div className="overflow-auto">
				{(restaurant?.operating_hours as OperatingHoursType[])?.map((rawItem, index) => {
					const item = rawItem as OperatingHours;
					return (
						<div key={item?.day} className={`w-lg min-w-full h-16 gap-6 flex items-center py-4 border-b border-gray-200 dark:border-gray-700 ${index == 0 ? 'border-t' : ''}`}>
							<div className="w-20">
								<span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
									{t(`settings.days.${item?.day}`)}
								</span>
							</div>

							<Toggle
								enabled={!item?.is_closed}
								onChange={(enabled) => updateOperatingHours(index, { is_closed: !enabled })}
							/>
							<div className="flex-1 flex items-center justify-end gap-2">
								{!item?.is_closed && (
									<>
										<input
											type="time"
											value={item?.opening_time ?? ''}
											onChange={(e) => updateOperatingHours(index, { opening_time: e.target.value })}
											className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
										/>
										<span className="text-gray-500">-</span>
										<input
											type="time"
											value={item?.closing_time ?? ''}
											onChange={(e) => updateOperatingHours(index, { closing_time: e.target.value })}
											className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
										/>
									</>
								)}

								{item?.is_closed && (
									<span className="pe-2 text-gray-500 dark:text-gray-400 italic">
										{t('settings.closed')}
									</span>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</Card>
	</div>
};

export default OperatingHoursSettings;